import { Mail, Phone, MapPin, Linkedin, Globe, Github, Calendar, Award } from 'lucide-react'
import { getColorClasses } from '@/lib/templateColors'

interface ResumeTemplateProps {
  data: any
  language?: string
  labels: any
  color?: string
}


export default function MinimalTemplate({ data, labels, color = 'blue' }: ResumeTemplateProps) {
  const { personalInfo, summary, experience, education, skills, projects, certifications } = data
  const colors = getColorClasses(color)

  return (

      <div className="bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
        <div id="resume-preview" className="bg-white p-12" style={{ width: '8.5in', minHeight: '11in', margin: '0 auto' }}>
          <div className="space-y-6 text-gray-900">
            {/* Header */}
            <div className={`text-center space-y-2 pb-4 border-b ${colors.sectionBorder}`}>
              <h1 className={`text-3xl font-bold tracking-wide ${colors.sectionTitle}`}>{personalInfo.fullName || 'YOUR NAME'}</h1>
              <div className="text-xs text-gray-600 space-y-1">
                <div>{[personalInfo.email, personalInfo.phone, personalInfo.address].filter(Boolean).join(' • ')}</div>
                <div>{[personalInfo.linkedin, personalInfo.website, personalInfo.github].filter(Boolean).join(' • ')}</div>
              </div>
            </div>

            {/* Summary */}
            {summary && (
              <div>
                <p className="text-sm leading-relaxed text-justify">{summary}</p>
              </div>
            )}

            {/* Experience */}
            {experience && experience.length > 0 && (
              <div>
                <h2 className={`text-sm font-bold mb-3 uppercase tracking-widest ${colors.sectionTitle}`}>Experience</h2>
                <div className="space-y-4 text-sm">
                  {experience.map((exp: any) => (
                    <div key={exp.id}>
                      <div className={`flex justify-between font-semibold mb-1 ${colors.accentText}`}>
                        <span>{exp.jobTitle} | {exp.company}</span>
                        <span className="text-gray-600">{exp.startDate} - {exp.endDate || labels.present}</span>
                      </div>
                      <div className="text-gray-700 whitespace-pre-line leading-relaxed">{exp.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Skills */}
            {skills && skills.length > 0 && (
              <div>
                <h2 className={`text-sm font-bold mb-3 uppercase tracking-widest ${colors.sectionTitle}`}>{labels.skills}</h2>
                <div className="text-sm space-y-1">
                  {skills.map((skillGroup: any, index: number) => (
                    <div key={index}>
                      <span className={`font-semibold ${colors.accentText}`}>{skillGroup.category}:</span> {skillGroup.skills?.join(', ')}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Education */}
            {education && education.length > 0 && (
              <div>
                <h2 className={`text-sm font-bold mb-3 uppercase tracking-widest ${colors.sectionTitle}`}>Education</h2>
                <div className="space-y-2 text-sm">
                  {education.map((edu: any) => (
                    <div key={edu.id}>
                      <div className={`flex justify-between font-semibold ${colors.accentText}`}>
                        <span>{edu.degree}</span>
                        <span className="text-gray-600">{edu.graduationDate}</span>
                      </div>
                      <div>{edu.institution}{edu.gpa && ` | GPA: ${edu.gpa}`}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Projects */}
            {projects && projects.length > 0 && (
              <div>
                <h2 className={`text-sm font-bold mb-3 uppercase tracking-widest ${colors.sectionTitle}`}>Projects</h2>
                <div className="space-y-3 text-sm">
                  {projects.map((project: any) => (
                    <div key={project.id}>
                      <h3 className={`font-semibold ${colors.accentText}`}>{project.name}</h3>
                      <p className="text-gray-700">{project.description}</p>
                      {project.technologies && (
                        <div className="text-xs text-gray-600 mt-1">{project.technologies.join(' • ')}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Certifications */}
            {certifications && certifications.length > 0 && (
              <div>
                <h2 className={`text-sm font-bold mb-3 uppercase tracking-widest ${colors.sectionTitle}`}>Certifications</h2>
                <div className="space-y-1 text-sm">
                  {certifications.map((cert: any) => (
                    <div key={cert.id}>
                      <span className={`font-semibold ${colors.accentText}`}>{cert.name}</span> - {cert.issuer}, {cert.date}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
  )
}
